/**
 * Minimal video-player-style scrubber bar for FTGS segment playback -
 * play/pause circle button + a thin draggable progress line + a "current /
 * total" time readout, matching the reference screenshot's visual style
 * exactly (dark full-width bar, circular play/pause glyph, thin white
 * track+thumb). The trailing icon cluster from the reference (volume, loop,
 * cast, info) is intentionally NOT implemented yet - out of scope for now,
 * left as a later addition.
 *
 * Deliberately NOT part of EditorUI's chromeElements (see editor.ts's
 * setChromeVisible) - this is FTGS's own playback UI, not SuperSplat editor
 * chrome, so it stays visible even in the default clean-viewer mode.
 *
 * Owns no playback state itself - purely a view. The caller (main.ts's
 * ?ftgsDebug block) wires `onTogglePlay`/`onSeek` to its own playT/paused
 * locals and calls `update()` every frame to reflect the current state.
 */

import { Container, Label } from '@playcanvas/pcui';

import { createPointerMoveFilter } from './pointer-move-filter';

const PLAY_SVG = '<svg viewBox="0 0 24 24"><path d="M8 5v14l11-7z" fill="currentColor"/></svg>';
const PAUSE_SVG = '<svg viewBox="0 0 24 24"><path d="M7 5h4v14H7zM13 5h4v14h-4z" fill="currentColor"/></svg>';

// One corner-arrow shape (bracket + shaft, pointing away from center out of
// the top-right corner), reused 4x via rotation around the 24x24 viewBox's
// center (12,12) - draws four identical outward-pointing arrows, one per
// corner, matching the reference icon.
const cornerArrows = (inward: boolean) => {
    // outward: bracket sits AT the corner, shaft points away from center.
    // inward: bracket sits near center, shaft points away from the corner
    // toward center - same shapes, just swapped which end is which.
    const bracket = inward ? 'points="9 3 3 3 3 9"' : 'points="15 3 21 3 21 9"';
    const shaft = inward ? 'x1="3" y1="3" x2="10" y2="10"' : 'x1="21" y1="3" x2="14" y2="10"';
    const arrow = `<polyline ${bracket}/><line ${shaft}/>`;
    return [0, 90, 180, 270].map(deg => (deg === 0 ? arrow : `<g transform="rotate(${deg} 12 12)">${arrow}</g>`)).join('');
};
const FULLSCREEN_ENTER_SVG = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${cornerArrows(false)}</svg>`;
const FULLSCREEN_EXIT_SVG = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${cornerArrows(true)}</svg>`;

// hamburger/stats-toggle icon - 3 evenly-spaced rounded horizontal bars
const STATS_SVG = '<svg viewBox="0 0 24 24" fill="currentColor"><rect x="3" y="4" width="18" height="3" rx="1.5"/><rect x="3" y="10.5" width="18" height="3" rx="1.5"/><rect x="3" y="17" width="18" height="3" rx="1.5"/></svg>';

// VR headset glyph - rounded goggle body with a notch cut out of the
// bottom-center edge (the "nose bridge" gap), same stroke style as the
// fullscreen icon so the icon cluster reads as one visual family.
const VR_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 9a3 3 0 0 1 3-3h10a3 3 0 0 1 3 3v3a3 3 0 0 1-3 3h-2.2a1.8 1.8 0 0 1-1.6-1l-.2-.4a2 2 0 0 0-3.6 0l-.2.4a1.8 1.8 0 0 1-1.6 1H7a3 3 0 0 1-3-3z"/></svg>';

// stereo/dual-view mode glyph - two side-by-side rounded rectangles (one per
// eye), same stroke style as the fullscreen/VR icons. Toggles the
// Odyssey-style side-by-side split view (see camera.ts setSplitView).
const STEREO_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="5" width="8" height="14" rx="1.5"/><rect x="13" y="5" width="8" height="14" rx="1.5"/></svg>';

// face-tracker glyph - a head outline with two eye dots, same stroke
// style as the icon cluster. Toggles the eye_tracking WebSocket client
// (see ftgs/ftgs-facetracker-client.ts); coloring reflects the live
// connection state via setFaceTrackerState().
const FACETRACKER_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="8"/><circle cx="9" cy="10.5" r="0.6" fill="currentColor"/><circle cx="15" cy="10.5" r="0.6" fill="currentColor"/><path d="M9.5 15a3.5 3.5 0 0 0 5 0"/></svg>';

/** how long the mouse must sit still before the bar starts fading out */
const IDLE_HIDE_DELAY_MS = 2000;

class FtgsPlaybackBar extends Container {
    private playButton: HTMLButtonElement;
    private track: HTMLDivElement;
    private fill: HTMLDivElement;
    private thumb: HTMLDivElement;
    private timeLabel: Label;
    private scrubbing = false;
    private duration = 1;
    /**
     * Tracks which icon is currently shown, separately from the DOM. Reading
     * `playButton.innerHTML` back is NOT a reliable way to check this - the
     * browser normalizes serialized markup (e.g. `<path .../>` round-trips
     * as `<path ...></path>`), so a literal-string comparison against
     * PAUSE_SVG/PLAY_SVG is never equal after the first assignment. That
     * made update() reassign innerHTML on every single frame (60-90x/sec)
     * even when the icon hadn't changed - which, worse than just wasteful,
     * could tear the button's child SVG node out from under an in-progress
     * click (mousedown on the old node, but it's replaced by mouseup),
     * intermittently swallowing the click. This flag makes the "did it
     * actually change" check correct instead of racy.
     */
    private isPauseIcon: boolean | null = null;

    private stereoButton: HTMLButtonElement;
    private isStereoActive = false;

    private faceTrackerButton: HTMLButtonElement;
    private isFaceTrackerOn = false;
    /** last state class applied - same staleness-avoidance reasoning as isPauseIcon */
    private faceTrackerState: 'off' | 'connecting' | 'connected' = 'off';

    private fullscreenButton: HTMLButtonElement;
    /** same staleness-avoidance reasoning as isPauseIcon above */
    private isFullscreenExitIcon: boolean | null = null;

    private vrButton: HTMLButtonElement;
    /**
     * Source of truth is the REAL WebXR session state (main.ts calls
     * setVrActive() from app.xr's 'start'/'end' events), not just "the
     * button was clicked" - a VR session can also end from inside the
     * headset (its own system menu), which this needs to reflect too. Same
     * staleness-avoidance reasoning as isPauseIcon/isFullscreenExitIcon.
     */
    private isVrActive: boolean | null = null;
    /** glasses-mode inputs kept so setGlasses()/setGlassesTracking() can each re-render the VR button */
    private glassesName: string | null = null;
    private glassesActive = false;
    private glassesPermission: 'granted' | 'prompt' | 'denied' | 'unsupported' = 'granted';
    private glassesTracking: 'off' | 'connecting' | 'connected' = 'off';

    // auto-hide-on-idle state (see the constructor's mouse-activity listener)
    private idle = false;
    private hoveringBar = false;
    private hideTimeoutId: number | null = null;

    private statsButton: HTMLButtonElement;

    /** caller-supplied handlers - left null until main.ts wires them up */
    onTogglePlay: (() => void) | null = null;
    onSeek: ((t: number) => void) | null = null;
    onToggleStats: (() => void) | null = null;
    /**
     * main.ts owns the actual app.xr.start()/end() calls (needs the camera
     * entity + graphics device, neither of which this view has) - this is
     * just the click signal. Left null (button effectively inert) until
     * main.ts wires it up, e.g. because WebXR isn't available at all.
     */
    onToggleVr: (() => void) | null = null;
    /**
     * fired with the NEW stereo state on each click of the stereo button
     * (true = split view requested, false = restore single view). main.ts
     * owns the actual camera/render-pass switch - this is just the signal.
     */
    onToggleStereo: ((active: boolean) => void) | null = null;
    /**
     * fired with the NEW on/off intent on each click of the face-tracker
     * button (true = start the eye_tracking WebSocket client, false =
     * stop it). main.ts owns the actual client; the button's coloring
     * follows the REAL connection state via setFaceTrackerState(), not
     * the click.
     */
    onToggleFaceTracker: ((on: boolean) => void) | null = null;
    /** fired when the idle auto-hide fades the bar out (true) or back in (false) - main.ts hides the version tag along with it */
    onIdleChange: ((idle: boolean) => void) | null = null;

    constructor(args: Record<string, unknown> = {}) {
        super({ ...args, id: 'ftgs-playback-bar' });

        // this bar sits directly over the 3D canvas - without this, dragging
        // the scrubber or clicking play would also reach the canvas's own
        // pointer handlers underneath and orbit/pan the camera at the same
        // time (same pattern as camera-info-overlay.ts).
        ['pointerdown', 'pointerup', 'pointermove', 'wheel', 'dblclick'].forEach((eventName) => {
            this.dom.addEventListener(eventName, (event: Event) => event.stopPropagation());
        });

        const playButton = document.createElement('button');
        playButton.type = 'button';
        playButton.className = 'ftgs-playback-play';
        playButton.innerHTML = PAUSE_SVG;
        // dynamic action tooltip, same convention as every other toggle in
        // this bar: names what a click does next - kept in sync with the
        // icon swap in update() below (starts as the pause icon = playing)
        playButton.dataset.tooltip = 'Stop [Key: Space]';
        this.isPauseIcon = true;
        playButton.addEventListener('click', () => this.onTogglePlay?.());
        this.dom.appendChild(playButton);

        const track = document.createElement('div');
        track.className = 'ftgs-playback-track';

        const fill = document.createElement('div');
        fill.className = 'ftgs-playback-fill';
        track.appendChild(fill);

        const thumb = document.createElement('div');
        thumb.className = 'ftgs-playback-thumb';
        track.appendChild(thumb);

        this.dom.appendChild(track);

        const timeLabel = new Label({ class: 'ftgs-playback-time' });
        this.append(timeLabel);

        // fullscreen + stats buttons share a tighter sub-group so they sit
        // closer to each other than the bar's normal gap (14px) - they
        // read as one cluster of secondary controls, distinct from the
        // primary play button and the time readout.
        const iconGroup = document.createElement('div');
        iconGroup.className = 'ftgs-playback-icon-group';
        this.dom.appendChild(iconGroup);

        // stereo/dual-view mode toggle (see STEREO_SVG comment above).
        // Leftmost in the icon cluster so it sits closest to the
        // timeslider/track. Click flips the state and notifies main.ts;
        // clicking again restores the single full-width view.
        const stereoButton = document.createElement('button');
        stereoButton.type = 'button';
        stereoButton.className = 'ftgs-playback-stereo';
        stereoButton.innerHTML = STEREO_SVG;
        // dynamic action tooltip (user preference, unlike the static
        // Play/Stop / Fullscreen-Windowed ones): names the view a click
        // SWITCHES TO - kept in sync with the state in setStereoActive()
        stereoButton.dataset.tooltip = 'Stereo View [Key: V]';
        stereoButton.addEventListener('click', () => this.toggleStereo());
        iconGroup.appendChild(stereoButton);

        // face-tracker toggle (see FACETRACKER_SVG comment above). Sits
        // next to the stereo button - the two are used together for the
        // head-tracked Odyssey setup. Click signals intent to main.ts;
        // the 3-state coloring (off/connecting/connected) tracks the real
        // WebSocket state via setFaceTrackerState().
        const faceTrackerButton = document.createElement('button');
        faceTrackerButton.type = 'button';
        faceTrackerButton.className = 'ftgs-playback-facetracker';
        faceTrackerButton.innerHTML = FACETRACKER_SVG;
        faceTrackerButton.dataset.tooltip = 'Face Tracking [Key: F]';
        faceTrackerButton.addEventListener('click', () => {
            this.isFaceTrackerOn = !this.isFaceTrackerOn;
            this.onToggleFaceTracker?.(this.isFaceTrackerOn);
        });
        iconGroup.appendChild(faceTrackerButton);
        this.faceTrackerButton = faceTrackerButton;

        // fullscreen toggle
        const fullscreenButton = document.createElement('button');
        fullscreenButton.type = 'button';
        fullscreenButton.className = 'ftgs-playback-fullscreen';
        fullscreenButton.innerHTML = FULLSCREEN_ENTER_SVG;
        // dynamic action tooltip, same convention as the stereo button:
        // names the state a click switches TO - kept in sync with the icon
        // in onFullscreenChange below
        fullscreenButton.dataset.tooltip = 'Fullscreen [Key: F11]';
        fullscreenButton.addEventListener('click', () => {
            // both reject (a real Promise rejection, not a thrown
            // exception) if the browser refuses - e.g. no permission, or
            // fullscreen disabled by an embedding page's iframe policy -
            // caught and logged rather than left as an unhandled rejection
            const request = document.fullscreenElement ? document.exitFullscreen() : document.documentElement.requestFullscreen();
            request.catch((error: unknown) => {
                console.warn('[FtgsPlaybackBar] fullscreen toggle failed:', error);
            });
        });
        iconGroup.appendChild(fullscreenButton);

        // reflects ACTUAL fullscreen state, not just "the button was
        // clicked" - fires on ESC-to-exit and any other way fullscreen
        // changes too, not only via this button
        const onFullscreenChange = () => {
            const active = !!document.fullscreenElement;
            if (this.isFullscreenExitIcon !== active) {
                fullscreenButton.innerHTML = active ? FULLSCREEN_EXIT_SVG : FULLSCREEN_ENTER_SVG;
                // in windowed mode offer "Fullscreen", in fullscreen offer
                // "Windowed" - the state a click switches TO
                fullscreenButton.dataset.tooltip = active ? 'Windowed [Key: Esc]' : 'Fullscreen [Key: F11]';
                this.isFullscreenExitIcon = active;
            }
            // 창이 다른 화면(글래스)으로 넘어가면 마우스 이벤트가 더 오지 않을 수 있으므로 숨김
            // 타이머를 다시 걸어 둠 - 타이머가 실제 호버 여부를 다시 확인함 (scheduleHide)
            if (!this.scrubbing) {
                this.scheduleHide();
            }
        };
        document.addEventListener('fullscreenchange', onFullscreenChange);

        // VR toggle - actual app.xr.start()/end() calls happen in main.ts
        // (this view has no access to the camera entity/graphics device
        // they need); this button only signals intent and reflects
        // whatever state setVrActive() is told after the fact.
        const vrButton = document.createElement('button');
        vrButton.type = 'button';
        vrButton.className = 'ftgs-playback-vr';
        vrButton.innerHTML = VR_SVG;
        // dynamic action tooltip, same convention as the stereo/fullscreen
        // buttons: names the state a click switches TO - kept in sync with
        // the real WebXR session state in setVrActive()
        vrButton.dataset.tooltip = 'VR [Key: X]';
        vrButton.addEventListener('click', () => this.onToggleVr?.());
        iconGroup.appendChild(vrButton);

        // stats overlay toggle - rightmost element in the bar
        const statsButton = document.createElement('button');
        statsButton.type = 'button';
        statsButton.className = 'ftgs-playback-stats';
        statsButton.innerHTML = STATS_SVG;
        // dynamic action tooltip, same convention as the other toggles:
        // names what a click does next - the overlay starts hidden, and
        // main.ts reports changes via setStatsVisible()
        statsButton.dataset.tooltip = 'Show Stats [Key: `]';
        statsButton.addEventListener('click', () => this.onToggleStats?.());
        iconGroup.appendChild(statsButton);

        // scrubbing - same pointer-capture drag pattern as timeline-panel.ts's Ticks
        const seekFromEvent = (event: PointerEvent) => {
            const rect = track.getBoundingClientRect();
            const frac = rect.width > 0 ? Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width)) : 0;
            this.onSeek?.(frac * this.duration);
        };

        track.addEventListener('pointerdown', (event: PointerEvent) => {
            if (!this.scrubbing && event.isPrimary) {
                this.scrubbing = true;
                track.setPointerCapture(event.pointerId);
                seekFromEvent(event);
            }
        });
        track.addEventListener('pointermove', (event: PointerEvent) => {
            if (this.scrubbing) {
                seekFromEvent(event);
            }
        });
        track.addEventListener('pointerup', (event: PointerEvent) => {
            if (this.scrubbing && event.isPrimary) {
                track.releasePointerCapture(event.pointerId);
                this.scrubbing = false;
            }
        });

        this.playButton = playButton;
        this.track = track;
        this.fill = fill;
        this.thumb = thumb;
        this.timeLabel = timeLabel;
        this.stereoButton = stereoButton;
        this.fullscreenButton = fullscreenButton;
        this.vrButton = vrButton;
        this.statsButton = statsButton;

        // Auto-hide on idle: fades out after IDLE_HIDE_DELAY_MS of no mouse
        // movement anywhere in the window, fades back in on the next
        // movement. Listened for on `window` (not `this.dom`) since our own
        // pointer events are stopPropagation'd (see above) and wouldn't
        // otherwise bubble past this element - a window-level listener is
        // unaffected by that (separate listener target, not a bubble
        // target) and is what actually needs to observe "is the mouse
        // moving anywhere in the browser at all", not just over the bar.
        // 위치가 그대로인 이동 신호는 무시 (pointer-move-filter.ts 참고)
        const isRealMove = createPointerMoveFilter();
        const onWindowPointerMove = (event: PointerEvent) => {
            if (!isRealMove(event)) {
                return;
            }
            this.setHidden(false);
            this.scheduleHide();
        };
        window.addEventListener('pointermove', onWindowPointerMove);

        // Hovering directly over the bar (even without moving) should keep
        // it visible and pause the countdown - otherwise a user resting
        // their cursor on the bar to read the time/scrub would watch it
        // fade out from under them.
        this.dom.addEventListener('pointerenter', () => {
            this.hoveringBar = true;
            this.setHidden(false);
            this.clearHideTimer();
        });
        this.dom.addEventListener('pointerleave', () => {
            this.hoveringBar = false;
            if (!this.scrubbing) {
                this.scheduleHide();
            }
        });

        // start the countdown immediately so a page load with no mouse
        // movement at all still fades out after the same delay
        this.scheduleHide();

        this.on('destroy', () => {
            window.removeEventListener('pointermove', onWindowPointerMove);
            document.removeEventListener('fullscreenchange', onFullscreenChange);
            this.clearHideTimer();
        });
    }

    private clearHideTimer() {
        if (this.hideTimeoutId !== null) {
            window.clearTimeout(this.hideTimeoutId);
            this.hideTimeoutId = null;
        }
    }

    private scheduleHide() {
        this.clearHideTimer();
        this.hideTimeoutId = window.setTimeout(() => {
            this.hideTimeoutId = null;
            // 기억해 둔 hoveringBar 는 pointerleave 를 놓치면 켜진 채 남음(2026-10-31: PC 화면에서 VR
            // 아이콘을 눌러 창이 글래스로 넘어가면 마우스가 벗어나는 신호가 오지 않음) - 그래서 숨기기
            // 직전에 브라우저의 실제 상태(:hover, 입체 소프트웨어 커서는 ftgs-stereo-hover)로 다시 확인
            if (this.hoveringBar && !this.dom.matches(':hover, .ftgs-stereo-hover')) {
                this.hoveringBar = false;
            }
            // don't hide out from under an active hover/drag
            if (!this.hoveringBar && !this.scrubbing) {
                this.setHidden(true);
            }
        }, IDLE_HIDE_DELAY_MS);
    }

    private setHidden(hidden: boolean) {
        if (this.idle === hidden) {
            return;
        }
        this.idle = hidden;
        this.dom.classList.toggle('ftgs-playback-bar-idle', hidden);
        this.onIdleChange?.(hidden);
    }

    /** Reflect the REAL WebXR session state - call from main.ts's app.xr 'start'/'end' listeners, not just on click, so a session ended from inside the headset's own menu still updates the icon. */
    setStereoActive(active: boolean) {
        if (this.isStereoActive === active) {
            return;
        }
        this.isStereoActive = active;
        this.stereoButton.classList.toggle('ftgs-playback-stereo-active', active);
        // tooltip names what a click switches TO: in single view offer
        // "Stereo View", in stereo offer "1-View"
        this.stereoButton.dataset.tooltip = active ? '1-View [Key: V]' : 'Stereo View [Key: V]';
    }

    /** what a click on the stereo button does - also main.ts's V key (2026-10-26) */
    toggleStereo() {
        this.setStereoActive(!this.isStereoActive);
        this.onToggleStereo?.(this.isStereoActive);
    }

    /**
     * Reflect the face-tracker client's REAL state - call from main.ts's
     * FtgsFaceTrackerClient.onStateChange, not just on click, so a lost
     * connection (tracker app closed) downgrades the coloring too.
     * 'off' = idle, 'connecting' = client on but no tracker yet (amber),
     * 'connected' = pose stream live (green).
     */
    setFaceTrackerState(state: 'off' | 'connecting' | 'connected') {
        if (this.faceTrackerState === state) {
            return;
        }
        this.faceTrackerState = state;
        this.isFaceTrackerOn = state !== 'off';
        this.faceTrackerButton.classList.toggle('ftgs-playback-facetracker-connecting', state === 'connecting');
        this.faceTrackerButton.classList.toggle('ftgs-playback-facetracker-connected', state === 'connected');
        // tooltip names what a click does next, same convention as the
        // other toggles
        this.faceTrackerButton.dataset.tooltip = state === 'off' ? 'Face Tracking [Key: F]' : 'Stop Face Tracking [Key: F]';
    }

    setVrActive(active: boolean) {
        if (this.isVrActive === active) {
            return;
        }
        this.isVrActive = active;
        this.vrButton.classList.toggle('ftgs-playback-vr-active', active);
        // outside VR offer "VR", inside VR offer "Non-VR" - the state a
        // click switches TO (session-ended-from-headset updates land here
        // too, via main.ts's app.xr 'end' listener)
        this.vrButton.dataset.tooltip = active ? 'Non-VR [Key: X]' : 'VR [Key: X]';
    }

    /**
     * X 키(main.ts) - VR 아이콘을 누른 것과 같음. 아이콘이 숨겨졌거나 꺼진(글래스 미연결) 상태면
     * 클릭도 안 되므로 아무것도 하지 않음.
     */
    toggleVr() {
        if (this.vrButton.hidden || this.vrButton.disabled) {
            return;
        }
        this.onToggleVr?.();
    }

    /** Call once at startup with the result of app.xr.isAvailable(XRTYPE_VR) (or false if WebXR/the XrManager isn't present at all) - hides the button entirely rather than leaving a dead control that does nothing when clicked. */
    setVrAvailable(available: boolean) {
        this.vrButton.hidden = !available;
    }

    /**
     * Glasses mode (see watchGlassesScreen / main.ts): the VR button is
     * enabled only while XREAL glasses are plugged in as a display, and its
     * tooltip names the connected product. `name` null = nothing connected
     * -> greyed out. `active` = the viewer is currently fullscreen on the
     * glasses (click exits).
     */
    setGlasses(name: string | null, active: boolean, permission: 'granted' | 'prompt' | 'denied' | 'unsupported' = 'granted') {
        this.glassesName = name;
        this.glassesActive = active;
        this.glassesPermission = permission;
        this.renderGlasses();
    }

    /**
     * Glasses head tracking (xreal_eye_tracker on ws 27018, see main.ts):
     * 'connected' turns the VR icon green while glasses are detected -
     * the face tracker keeps its own button. Only the icon colour changes;
     * glasses mode itself stays the background highlight.
     */
    setGlassesTracking(state: 'off' | 'connecting' | 'connected') {
        this.glassesTracking = state;
        this.renderGlasses();
    }

    private renderGlasses() {
        const name = this.glassesName;
        const active = this.glassesActive;
        const permission = this.glassesPermission;
        const tracking = !!name && this.glassesTracking === 'connected';
        this.vrButton.hidden = false;
        // not asked yet: keep it clickable so the click can ask for the
        // window-management permission detection needs
        // - but it always LOOKS off (greyed) until glasses are connected
        const askable = !name && permission === 'prompt';
        this.vrButton.disabled = !name && !askable;
        this.vrButton.classList.toggle('ftgs-playback-vr-off', !name);
        this.vrButton.classList.toggle('ftgs-playback-vr-active', !!name && active);
        this.vrButton.classList.toggle('ftgs-playback-vr-tracking', tracking);
        const suffix = tracking ? ' · Head Tracking' : '';
        this.vrButton.dataset.tooltip =
            name ? (active ? `Exit VR: ${name}${suffix} [Key: X]` : `VR: ${name}${suffix} [Key: X]`) :
                askable ? 'VR (Disconnected - click to detect) [Key: X]' :
                    permission === 'denied' ? 'VR (Detection blocked)' : 'VR (Disconnected)';
    }

    /** Reflect the stats overlay's ACTUAL visibility (the overlay itself lives in main.ts) so the button tooltip offers the action a click performs next: hidden -> "Show Stats", shown -> "Hide Stats". */
    setStatsVisible(visible: boolean) {
        this.statsButton.dataset.tooltip = visible ? 'Hide Stats [Key: `]' : 'Show Stats [Key: `]';
    }

    /** Refresh the displayed position/duration/play-state - call every frame (or whenever playT changes) from the playback driver. */
    update(t: number, duration: number, playing: boolean) {
        this.duration = duration;
        const frac = duration > 0 ? Math.max(0, Math.min(1, t / duration)) : 0;
        const pct = `${(frac * 100).toFixed(3)}%`;

        this.fill.style.width = pct;
        this.thumb.style.left = pct;
        this.timeLabel.text = `${t.toFixed(1)}s / ${duration.toFixed(1)}s`;

        if (this.isPauseIcon !== playing) {
            this.playButton.innerHTML = playing ? PAUSE_SVG : PLAY_SVG;
            // playing -> a click stops; stopped -> a click plays
            this.playButton.dataset.tooltip = playing ? 'Stop [Key: Space]' : 'Play [Key: Space]';
            this.isPauseIcon = playing;
        }
    }
}

export { FtgsPlaybackBar };
